import React, { useEffect, useState } from "react";
import { useTranslation } from "../../hooks/useTranslation";

/**
 * ErrorModal — clean, framework-agnostic modal that surfaces a recoverable
 * error to the user. Optionally counts down before redirecting somewhere safe.
 *
 * Props (kept compatible with existing call-sites):
 *  - show, onClose            : visibility + close handler
 *  - title, message           : strings (translated keys used as defaults)
 *  - buttonText               : CTA label
 *  - redirectUrl              : URL the CTA navigates to (or null to just close)
 *  - autoRedirectDelay        : seconds before auto-close/redirect
 *  - showCountdown            : whether to render the progress strip
 *  - variant                  : kept for back-compat; ignored visually
 *  - icon, size, centered,
 *    animation                : kept for back-compat; ignored visually
 */
const ErrorModal = ({
  show,
  onClose,
  title,
  message,
  redirectUrl,
  buttonText,
  autoRedirectDelay,
  showCountdown = true,
}) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(autoRedirectDelay);
  const [progress, setProgress] = useState(100);

  const modalTitle = title || t("modals.error.defaultTitle");
  const modalMessage = message || t("modals.error.defaultMessage");
  const modalButtonText = buttonText || t("modals.error.button");

  useEffect(() => {
    if (!show || !autoRedirectDelay || !redirectUrl) return undefined;

    setCountdown(autoRedirectDelay);
    setProgress(100);

    const tick = 100;
    const decrement = 100 / (autoRedirectDelay * (1000 / tick));
    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 0.1) {
          clearInterval(id);
          handleRedirect();
          return 0;
        }
        return prev - 0.1;
      });
      setProgress((prev) => (prev <= decrement ? 0 : prev - decrement));
    }, tick);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, autoRedirectDelay, redirectUrl]);

  const handleRedirect = () => {
    // Original behavior: when a redirect URL is configured, fire the close
    // handler (the parent owns navigation). We keep that contract.
    onClose?.();
  };

  if (!show) return null;

  return (
    <div className="altruon-err-overlay" role="alertdialog" aria-modal="true">
      <div className="altruon-err-card">
        {redirectUrl === null && (
          <button
            type="button"
            className="altruon-err-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Animated SVG warning icon */}
        <svg
          className="altruon-err-icon"
          viewBox="0 0 56 56"
          aria-hidden="true"
        >
          <circle
            cx="28" cy="28" r="26"
            fill="none"
            stroke="#ef4444"
            strokeWidth="3"
            strokeLinecap="round"
            className="altruon-err-icon-circle"
          />
          <line
            x1="28" y1="16" x2="28" y2="32"
            stroke="#ef4444"
            strokeWidth="3.5"
            strokeLinecap="round"
            className="altruon-err-icon-stem"
          />
          <circle
            cx="28" cy="40" r="2.5"
            fill="#ef4444"
            className="altruon-err-icon-dot"
          />
        </svg>

        <h2 className="altruon-err-title">{modalTitle}</h2>
        <p className="altruon-err-msg">{modalMessage}</p>

        {autoRedirectDelay && redirectUrl && (
          <div className="altruon-err-countdown">
            {showCountdown && (
              <div className="altruon-err-progress">
                <div
                  className="altruon-err-progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
            <p className="altruon-err-redirecting">
              {t("modals.error.redirecting", {
                variables: { count: Math.ceil(countdown) },
              })}
            </p>
          </div>
        )}

        <button
          type="button"
          className="altruon-err-cta"
          onClick={handleRedirect}
        >
          {modalButtonText}
        </button>
      </div>

      <style jsx>{`
        .altruon-err-overlay {
          position: fixed;
          inset: 0;
          z-index: 1050;
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          animation: errFade 0.2s ease;
        }
        .altruon-err-card {
          width: 100%;
          max-width: 400px;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          box-shadow: 0 20px 50px -10px rgba(15, 23, 42, 0.25);
          padding: 32px 28px 24px;
          text-align: center;
          position: relative;
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter",
            "Segoe UI", Helvetica, Arial, sans-serif;
          animation: errSlide 0.3s ease;
        }
        .altruon-err-close {
          position: absolute;
          top: 12px;
          right: 12px;
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 6px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s, color 0.15s;
        }
        .altruon-err-close:hover {
          background: #f1f5f9;
          color: #475569;
        }
        .altruon-err-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 20px;
          display: block;
          animation: errPop 0.45s ease 0.1s both;
        }
        .altruon-err-icon-circle {
          stroke-dasharray: 166;
          stroke-dashoffset: 166;
          animation: errDrawCircle 0.55s ease 0.2s forwards;
        }
        .altruon-err-icon-stem {
          stroke-dasharray: 18;
          stroke-dashoffset: 18;
          animation: errDrawStem 0.25s ease 0.7s forwards;
        }
        .altruon-err-icon-dot {
          opacity: 0;
          animation: errFadeDot 0.2s ease 0.95s forwards;
        }
        .altruon-err-title {
          font-size: 22px;
          font-weight: 700;
          letter-spacing: -0.015em;
          color: #0f172a;
          margin: 0 0 8px;
        }
        .altruon-err-msg {
          font-size: 14.5px;
          color: #64748b;
          line-height: 1.55;
          margin: 0 0 20px;
        }
        .altruon-err-countdown {
          margin-bottom: 16px;
        }
        .altruon-err-progress {
          width: 100%;
          height: 4px;
          background: #f1f5f9;
          border-radius: 999px;
          overflow: hidden;
          margin-bottom: 8px;
        }
        .altruon-err-progress-fill {
          height: 100%;
          background: #ef4444;
          border-radius: 999px;
          transition: width 0.1s linear;
        }
        .altruon-err-redirecting {
          font-size: 12.5px;
          color: #94a3b8;
          margin: 0;
        }
        .altruon-err-cta {
          width: 100%;
          padding: 12px 16px;
          background: #ef4444;
          color: #fff;
          font-size: 14.5px;
          font-weight: 600;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: background 0.15s, transform 0.1s;
          letter-spacing: 0.005em;
        }
        .altruon-err-cta:hover { background: #dc2626; }
        .altruon-err-cta:active { transform: translateY(1px); }
        @keyframes errFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes errSlide {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes errPop {
          0%   { transform: scale(0.7); opacity: 0; }
          70%  { transform: scale(1.08); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes errDrawCircle { to { stroke-dashoffset: 0; } }
        @keyframes errDrawStem   { to { stroke-dashoffset: 0; } }
        @keyframes errFadeDot    { to { opacity: 1; } }
      `}</style>
    </div>
  );
};

export default ErrorModal;
